import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { motion } from 'framer-motion';
import LeadMagnetModal from './LeadMagnetModal';

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    overflow-x: hidden;
  }
`;

const BookDownload = () => {
  const [showLeadMagnet, setShowLeadMagnet] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isBlinking, setIsBlinking] = useState(false);
  const characterRef = useRef(null);

  // Eye tracking effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (characterRef.current) {
        const rect = characterRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        const angleX = (e.clientX - centerX) / window.innerWidth;
        const angleY = (e.clientY - centerY) / window.innerHeight;
        
        setMousePosition({ 
          x: Math.max(-1, Math.min(1, angleX * 3)),
          y: Math.max(-1, Math.min(1, angleY * 3))
        });
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches[0]) {
        handleMouseMove(e.touches[0]);
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  // Blinking effect
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setIsBlinking(true);
      setTimeout(() => setIsBlinking(false), 200);
    }, 3000 + Math.random() * 2000);

    return () => clearInterval(blinkInterval);
  }, []);

  const allChapters = [
    { number: 1, title: "What is Marketing?", icon: "🔍", color: "#FF6B6B" },
    { number: 2, title: "Finding Your Audience", icon: "🎯", color: "#4ECDC4" },
    { number: 3, title: "Building Your Brand", icon: "🎨", color: "#FFD93D" },
    { number: 4, title: "Marketing Channels", icon: "📱", color: "#6BCF7F" },
    { number: 5, title: "Content That Clicks", icon: "✨", color: "#A8E6CF" },
    { number: 6, title: "Budgets & ROI", icon: "💰", color: "#FFB4B4" },
  ];

  return (
    <>
      <GlobalStyle />
      
      <SimpleHeader>
        <HeaderContainer>
          <Logo>📚 Marketing for Dummies</Logo>
          <GetGuideButton onClick={() => setShowLeadMagnet(true)}>
            Get Free Guide
          </GetGuideButton>
        </HeaderContainer>
      </SimpleHeader>
      
      <HeroSection>
        <BackgroundGradient />
        
        <MiniSun
          animate={{ 
            y: [0, -10, 0],
            rotate: [0, 5, -5, 0]
          }}
          transition={{ duration: 4, repeat: Infinity }}
        >
          <SunFace>
            <SunEyeContainer>
              <SunEye $isBlinking={isBlinking}>
                <SunPupil style={{
                  transform: `translate(${mousePosition.x * 3}px, ${mousePosition.y * 3}px)`
                }} />
              </SunEye>
              <SunEye $isBlinking={isBlinking}>
                <SunPupil style={{
                  transform: `translate(${mousePosition.x * 3}px, ${mousePosition.y * 3}px)`
                }} />
              </SunEye>
            </SunEyeContainer>
            <SunSmile />
          </SunFace>
          <SunRays>
            {[...Array(8)].map((_, i) => (
              <Ray key={i} style={{ transform: `rotate(${i * 45}deg)` }} />
            ))}
          </SunRays>
        </MiniSun>

        <FloatingRocket
          animate={{ 
            x: [0, 30, 0],
            y: [0, -40, 0],
            rotate: [0, 10, 0]
          }}
          transition={{ duration: 6, repeat: Infinity }}
        >
          🚀
        </FloatingRocket>

        <Container>
          <HeroContent>
            <ContentWrapper>
              <Badge
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                ✨ FREE GUIDE ✨
              </Badge>

              <Title>
                Marketing for
                <RainbowText> Dummies</RainbowText>
              </Title>

              <Subtitle>A Cartoon Guide to Business Growth</Subtitle>

              <Description>
                Stop feeling overwhelmed by marketing! Our fun, illustrated guide 
                breaks everything down into simple lessons anyone can follow.
              </Description>

              <CTAButton
                onClick={() => setShowLeadMagnet(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                GET YOUR FREE COPY!
                <ButtonGlow />
              </CTAButton>

              <TrustIndicators>
                <TrustItem>📖 20 Chapters</TrustItem>
                <TrustItem>🎨 Fully Illustrated</TrustItem>
                <TrustItem>⚡ Instant Download</TrustItem>
              </TrustIndicators>
            </ContentWrapper>

            <VisualWrapper>
              <CharacterContainer>
                <MainCharacter
                  ref={characterRef}
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <CharacterHead>
                    <Face>
                      <Eyes>
                        <Eye $isBlinking={isBlinking}>
                          <Pupil style={{
                            transform: `translate(${mousePosition.x * 5}px, ${mousePosition.y * 5}px)`
                          }} />
                        </Eye>
                        <Eye $isBlinking={isBlinking}>
                          <Pupil style={{
                            transform: `translate(${mousePosition.x * 5}px, ${mousePosition.y * 5}px)`
                          }} />
                        </Eye>
                      </Eyes>
                      <Smile />
                    </Face>
                  </CharacterHead>
                </MainCharacter>

                <SpeechBubble
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.5, type: "spring" }}
                >
                  <SpeechText>Hey! Ready to learn marketing the fun way? 🎉</SpeechText>
                  <BubbleTail />
                </SpeechBubble>
              </CharacterContainer>

              <BookContainer
                animate={{ 
                  rotateY: [-5, 5, -5],
                  y: [0, -5, 0]
                }}
                transition={{ duration: 5, repeat: Infinity }}
              >
                <Book3D>
                  <BookSpine />
                  <BookFront>
                    <BookCoverDesign>
                      <BookLabel>FREE GUIDE</BookLabel>
                      <BookIcon>📚</BookIcon>
                      <BookTitleText>Marketing for Dummies</BookTitleText>
                      <BookSubtitleText>A Cartoon Guide</BookSubtitleText>
                    </BookCoverDesign>
                  </BookFront>
                  <BookPages />
                  <BookBack />
                </Book3D>
              </BookContainer>
            </VisualWrapper>
          </HeroContent>
        </Container>
      </HeroSection>

      <ChaptersSection>
        <Container>
          <SectionHeader>
            <SectionTitle>What's Inside?</SectionTitle>
            <SectionSubtitle>20 chapters packed with marketing wisdom</SectionSubtitle>
          </SectionHeader>

          <ChapterGrid>
            {allChapters.map((chapter, index) => (
              <ChapterCard
                key={chapter.number}
                style={{ background: `${chapter.color}20` }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <ChapterIcon>{chapter.icon}</ChapterIcon>
                <ChapterInfo>
                  <ChapterNumber style={{ color: chapter.color }}>
                    CHAPTER {chapter.number}
                  </ChapterNumber>
                  <ChapterTitle>{chapter.title}</ChapterTitle>
                </ChapterInfo>
              </ChapterCard>
            ))}
          </ChapterGrid>

          <MoreChapters>
            <Sparkle>✨</Sparkle>
            Plus 14 more chapters on AI, Storytelling, Psychology & more!
            <Sparkle>✨</Sparkle>
          </MoreChapters>
        </Container>
      </ChaptersSection>

      <FinalCTA>
        <Container>
          <CTAContent>
            <CTATitle>Ready to Master Marketing?</CTATitle>
            <CTAText>Get your free illustrated guide and start learning today!</CTAText>
            <BigCTAButton
              onClick={() => setShowLeadMagnet(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              START LEARNING TODAY! 🎉
            </BigCTAButton>
          </CTAContent>
        </Container>
      </FinalCTA>

      {/* USE THE MODAL COMPONENT */}
      <LeadMagnetModal 
        isOpen={showLeadMagnet} 
        onClose={() => setShowLeadMagnet(false)} 
      />
    </>
  );
};

// Animations
const rainbow = keyframes`
  0% { color: #FF6B6B; }
  25% { color: #4ECDC4; }
  50% { color: #FFD93D; }
  75% { color: #6BCF7F; }
  100% { color: #FF6B6B; }
`;

const glow = keyframes`
  0% { opacity: 0; transform: translateX(-100%); }
  50% { opacity: 1; }
  100% { opacity: 0; transform: translateX(100%); }
`;

const SimpleHeader = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  z-index: 100;
  border-bottom: 2px solid #333;
  padding: 1rem 0;
  padding-top: calc(1rem + env(safe-area-inset-top));
`;

const HeaderContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const Logo = styled.div`
  font-size: 1.25rem;
  font-weight: 800;
  color: #333;
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const GetGuideButton = styled.button`
  padding: 0.5rem 1.5rem;
  background: #FFD93D;
  border: 2px solid #333;
  border-radius: 999px;
  font-weight: 700;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    background: #FF6B6B;
    color: white;
  }
  
  @media (max-width: 480px) {
    padding: 0.4rem 1rem;
    font-size: 0.875rem;
  }
`;

const HeroSection = styled.section`
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  padding: 6rem 0 2rem;
  
  @media (max-width: 768px) {
    padding: 8rem 0 2rem;
    padding-top: calc(8rem + env(safe-area-inset-top));
    min-height: calc(100vh - env(safe-area-inset-top));
  }
`;

const BackgroundGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #667EEA 0%, #764BA2 100%);
  opacity: 0.1;
`;

const MiniSun = styled(motion.div)`
  position: absolute;
  top: 100px;
  right: 20px;
  width: 60px;
  height: 60px;
  background: #FFD93D;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 30px rgba(255, 217, 61, 0.5);
  z-index: 50;
  
  @media (max-width: 768px) {
    top: calc(80px + env(safe-area-inset-top));
    right: 10px;
    width: 50px;
    height: 50px;
  }
`;

const SunFace = styled.div`
  position: relative;
  z-index: 2;
`;

const SunEyeContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 5px;
`;

const SunEye = styled.div`
  width: 10px;
  height: ${props => props.$isBlinking ? '2px' : '10px'};
  background: white;
  border-radius: 50%;
  position: relative;
  overflow: hidden;
  transition: height 0.2s ease;
`;

const SunPupil = styled.div`
  width: 4px;
  height: 4px;
  background: #333;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  margin-top: -2px;
  margin-left: -2px;
`;

const SunSmile = styled.div`
  width: 20px;
  height: 10px;
  border: 2px solid #333;
  border-top: none;
  border-radius: 0 0 20px 20px;
  margin: 0 auto;
`;

const SunRays = styled.div`
  position: absolute;
  inset: -10px;
`;

const Ray = styled.div`
  position: absolute;
  width: 2px;
  height: 15px;
  background: #FFD93D;
  left: 50%;
  top: -15px;
  transform-origin: bottom center;
`;

const FloatingRocket = styled(motion.div)`
  position: absolute;
  font-size: 2.5rem;
  bottom: 20%;
  left: 5%;
  opacity: 0.7;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  width: 100%;
  position: relative;
  z-index: 10;
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;
  
  @media (min-width: 968px) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  max-width: 500px;
  
  @media (max-width: 968px) {
    text-align: center;
    order: 2;
  }
`;

const VisualWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  
  @media (max-width: 968px) {
    order: 1;
    margin-top: 2rem;
  }
`;

const Badge = styled(motion.div)`
  display: inline-block;
  background: white;
  padding: 0.5rem 1.5rem;
  border-radius: 999px;
  font-weight: 700;
  color: #FF6B6B;
  margin-bottom: 1rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 900;
  color: #333;
  margin-bottom: 0.5rem;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const RainbowText = styled.span`
  animation: ${rainbow} 5s ease-in-out infinite;
  display: inline-block;
`;

const Subtitle = styled.p`
  font-size: clamp(1.25rem, 3vw, 1.5rem);
  color: #666;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const Description = styled.p`
  font-size: 1.125rem;
  color: #777;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const CTAButton = styled(motion.button)`
  position: relative;
  padding: 1.25rem 3rem;
  background: linear-gradient(135deg, #FF6B6B 0%, #FFD93D 100%);
  border: 3px solid #333;
  border-radius: 50px;
  font-size: 1.125rem;
  font-weight: 800;
  color: white;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 1rem 2rem;
    font-size: 1rem;
  }
`;

const ButtonGlow = styled.div`
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: ${glow} 3s infinite;
`;

const TrustIndicators = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 2rem;
  flex-wrap: wrap;
  
  @media (max-width: 968px) {
    justify-content: center;
  }
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #666;
  font-size: 0.9rem;
`;

const CharacterContainer = styled.div`
  position: relative;
`;

const MainCharacter = styled(motion.div)`
  width: 150px;
  height: 150px;
  background: linear-gradient(135deg, #FFD93D 0%, #FFB347 100%);
  border-radius: 50%;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  border: 3px solid #333;
  cursor: pointer;
`;

const CharacterHead = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Face = styled.div``;

const Eyes = styled.div`
  display: flex;
  gap: 25px;
  margin-bottom: 15px;
`;

const Eye = styled.div`
  width: 20px;
  height: ${props => props.$isBlinking ? '3px' : '20px'};
  background: white;
  border-radius: 50%;
  position: relative;
  overflow: hidden;
  transition: height 0.2s ease;
`;

const Pupil = styled.div`
  width: 8px;
  height: 8px;
  background: #333;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  margin-top: -4px;
  margin-left: -4px;
`;

const Smile = styled.div`
  width: 50px;
  height: 25px;
  border: 3px solid #333;
  border-top: none;
  border-radius: 0 0 50px 50px;
  margin: 0 auto;
`;

const SpeechBubble = styled(motion.div)`
  position: absolute;
  top: -70px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  padding: 0.875rem 1.25rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  font-weight: 600;
  color: #333;
  border: 2px solid #333;
  min-width: 200px;
  
  @media (max-width: 480px) {
    top: -60px;
    padding: 0.75rem 1rem;
    min-width: 180px;
  }
`;

const SpeechText = styled.div`
  font-size: 0.9rem;
  white-space: nowrap;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    white-space: normal;
    text-align: center;
  }
`;

const BubbleTail = styled.div`
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid #333;
  
  &::before {
    content: '';
    position: absolute;
    bottom: 2px;
    left: -6px;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid white;
  }
`;

const BookContainer = styled(motion.div)`
  perspective: 1000px;
  margin-top: 2rem;
`;

const Book3D = styled.div`
  width: 180px;
  height: 240px;
  position: relative;
  transform-style: preserve-3d;
  transform: rotateY(-20deg);
`;

const BookSpine = styled.div`
  position: absolute;
  width: 40px;
  height: 100%;
  background: linear-gradient(90deg, #6B46C1 0%, #553C9A 100%);
  left: -20px;
  transform: rotateY(-90deg) translateZ(20px);
  border-radius: 2px 0 0 2px;
  box-shadow: -5px 0 10px rgba(0, 0, 0, 0.3);
`;

const BookFront = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
  border-radius: 0 4px 4px 0;
  transform: translateZ(20px);
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const BookCoverDesign = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
`;

const BookLabel = styled.div`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const BookIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.2));
`;

const BookTitleText = styled.div`
  color: white;
  font-weight: 800;
  font-size: 1rem;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const BookSubtitleText = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.75rem;
  text-align: center;
`;

const BookPages = styled.div`
  position: absolute;
  width: calc(100% - 4px);
  height: calc(100% - 4px);
  background: linear-gradient(90deg, #F9FAFB 0%, #FFFFFF 100%);
  left: 2px;
  top: 2px;
  transform: translateZ(0px);
  border-radius: 0 2px 2px 0;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(
      0deg,
      #F0F0F0,
      #F0F0F0 1px,
      white 1px,
      white 3px
    );
  }
`;

const BookBack = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: #7C3AED;
  transform: translateZ(-20px);
  border-radius: 0 4px 4px 0;
`;

const ChaptersSection = styled.section`
  padding: 5rem 0;
  background: white;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 900;
  color: #333;
  margin-bottom: 0.5rem;
`;

const SectionSubtitle = styled.p`
  color: #666;
  font-size: 1.125rem;
`;

const ChapterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const ChapterCard = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  border-radius: 15px;
  border: 2px solid #333;
  cursor: pointer;
`;

const ChapterIcon = styled.div`
  font-size: 2.5rem;
`;

const ChapterInfo = styled.div``;

const ChapterNumber = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  letter-spacing: 1px;
`;

const ChapterTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #333;
`;

const MoreChapters = styled.div`
  text-align: center;
  font-size: 1.125rem;
  font-weight: 700;
  color: #666;
  padding: 2rem;
  background: #F9FAFB;
  border-radius: 20px;
  border: 2px dashed #333;
`;

const Sparkle = styled.span`
  font-size: 1.5rem;
  margin: 0 0.5rem;
`;

const FinalCTA = styled.section`
  padding: 5rem 0;
  background: linear-gradient(180deg, #FFE5CC 0%, #FFD4E5 100%);
`;

const CTAContent = styled.div`
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 2rem;
  font-weight: 900;
  color: #333;
  margin-bottom: 1rem;
`;

const CTAText = styled.p`
  font-size: 1.125rem;
  color: #666;
  margin-bottom: 2rem;
`;

const BigCTAButton = styled(motion.button)`
  padding: 1.25rem 2.5rem;
  background: #FFD93D;
  border: 3px solid #333;
  border-radius: 50px;
  font-size: 1.25rem;
  font-weight: 800;
  color: #333;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
`;

export default BookDownload;